import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { UploadImageInputStyled } from "./styled";
import { useDropzone } from "react-dropzone";
import { MdDelete } from "react-icons/md";
import { portalService } from "apiServices";
import { toast } from "react-toastify";
import { IMG_PATH } from "utils/constants/imgPath";

const UploadImageInput = ({
  theme_upload_image = false,
  value = [],
  onChange,
  onChangeCustom,
  acceptType = "cover",
  deleteApiUrl,
  maxMBFileSizeWarning = 10,
  fieldName,
  maxFiles = 1,
  name,
  errors,
  label,
  id,
  placeholder = "เลือกไฟล์",
  paramDelete,
  scaleLabel,
  isDelete = true,
  disabled = false,
  uploadApiUrl,
}) => {
  // States
  const [labelShow, setLabelShow] = useState(placeholder);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [fileList, setFileList] = useState([]);
  const [error, setError] = useState(null);

  // Helper: Get accepted file types
  const getAcceptedFileTypes = (type) => {
    const fileTypes = {
      cover: { "image/jpeg": [".jpeg", ".jpg", ".png"] },
      png: { "image/png": [".png"] },
      jpg: { "image/jpeg": [".jpeg", ".jpg"] },
      pdf: { "application/pdf": [".pdf"] },
      csv: { "text/csv": [".csv"] },
      json: { "application/json": [".json"] },
      "img-pdf": {
        "image/jpeg": [".jpeg", ".jpg", ".png"],
        "application/pdf": [".pdf"],
      },
      video: { "video/*": [] },
      audio: { "audio/mpeg": [".mp3"] },
    };
    return fileTypes[type] || fileTypes.cover;
  };

  // Helper: Validate file size
  const validateFileSize = (files) => {
    if (!maxMBFileSizeWarning) return true;

    const oversizedFiles = files.filter(
      (file) => file.size / 1024 / 1024 > maxMBFileSizeWarning
    );

    if (oversizedFiles.length > 0) {
      alert(
        `ไฟล์มีขนาดใหญ่เกิน ${maxMBFileSizeWarning}MB กรุณาเลือกไฟล์ที่มีขนาดเล็กกว่า`
      );
      return false;
    }
    return true;
  };

  // Helper: Create file item object
  const createFileItem = (file) => {
    const fileId = Date.now() + Math.random();
    return {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      originalFileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    };
  };

  // Helper: Update upload progress
  const updateProgress = (fileId, progress) => {
    if (typeof progress === "number" && progress >= 0 && progress <= 100) {
      setUploadProgress((prev) => ({
        ...prev,
        [fileId]: progress,
      }));
    }
  };

  // Helper: Update upload status
  const updateStatus = (fileId, status) => {
    setUploadStatus((prev) => ({
      ...prev,
      [fileId]: status,
    }));
  };

  // Helper: Trigger callbacks
  const triggerCallbacks = (updatedFileList) => {
    // ส่งเฉพาะไฟล์ที่อัพโหลดสำเร็จหรือไฟล์เดิม (ไม่ใช่ไฟล์ที่กำลังอัพโหลด)
    const completedFiles = updatedFileList.filter(
      (file) => !file.isNewUpload || uploadStatus[file.id] === "success"
    );
    onChange(completedFiles);
    onChangeCustom && onChangeCustom(completedFiles);
  };

  // Helper: Update label text
  const updateLabel = (count) => {
    if (count === 0) {
      setLabelShow(placeholder);
    } else {
      setLabelShow(`เลือกไฟล์ ${count} ไฟล์`);
    }
  };

  // Upload single file via API
  const uploadFileViaAPI = async (file, fileId) => {
    try {
      if (!uploadApiUrl) {
        toast.error("ไม่สามารถอัพโหลดไฟล์ได้ เนื่องจากไม่มี API ที่กำหนด");
      } else {
        updateStatus(fileId, "uploading");
        updateProgress(fileId, 0);
        const formData = new FormData();
        formData.append("image", file);
        let res = await uploadApiUrl(formData, (progress) => {
          updateProgress(fileId, progress);
        });
        if (res && res.status === 200) {
          updateStatus(fileId, "success");
          return {
            ...res.data,
            isNewUpload: false,
            fieldName: fieldName || name || "file",
            originalFileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            id: res.data.id || fileId,
          };
        } else {
          throw new Error("Upload failed - no data returned");
        }
      }
    } catch (error) {
      updateStatus(fileId, "error");
      throw error;
    }
  };

  // Handle file drop/selection
  const handleFileDrop = async (acceptedFiles) => {
    if (!acceptedFiles?.length) return;

    setError(null);

    // Check file limit
    const currentFileCount = fileList.length;
    const newFileCount = acceptedFiles.length;
    const totalFiles = currentFileCount + newFileCount;

    if (totalFiles > maxFiles) {
      const remainingSlots = maxFiles - currentFileCount;
      if (remainingSlots <= 0) {
        toast.error(`สามารถอัพโหลดได้สูงสุด ${maxFiles} ไฟล์เท่านั้น`);
        return;
      } else {
        toast.warning(
          `สามารถเลือกได้อีกเพียง ${remainingSlots} ไฟล์ (สูงสุด ${maxFiles} ไฟล์)`
        );
        acceptedFiles = acceptedFiles.slice(0, remainingSlots);
      }
    }

    // Validate file sizes
    if (!validateFileSize(acceptedFiles)) return;

    // Create file items immediately
    const newFileItems = acceptedFiles.map(createFileItem);

    // Add files to list immediately
    setFileList((prev) => [...prev, ...newFileItems]);
    updateLabel(fileList.length + newFileItems.length);

    // Process uploads or store files
    if (uploadApiUrl || portalService?.POST_SURVEY_UPLOAD_MEDIA) {
      await processUploads(newFileItems);
    } else {
      toast.error("ไม่สามารถอัพโหลดไฟล์ได้ เนื่องจากไม่มี API ที่กำหนด");
    }
  };

  // Process multiple file uploads
  const processUploads = async (fileItems) => {
    for (const fileItem of fileItems) {
      try {
        const uploadedFile = await uploadFileViaAPI(fileItem.file, fileItem.id);
        handleUploadSuccess(fileItem, uploadedFile);
      } catch (error) {
        console.error(`Upload failed for ${fileItem.name}:`, error);
        // Error status is already handled in uploadFileViaAPI
      }
    }
  };
  // Process uploaded file success
  const handleUploadSuccess = (fileItem, uploadedFile) => {
    // Update file list with uploaded data
    setFileList((prev) => {
      const updatedList = prev.map((item) =>
        item.id === fileItem.id ? { ...item, ...uploadedFile } : item
      );
      triggerCallbacks(updatedList);
      return updatedList;
    });
  };

  // Handle file removal
  const handleRemoveFile = async (fileToRemove, isUpdate) => {
    if (!fileToRemove) return;

    try {
      setError(null);

      // Delete from API if uploaded
      await deleteFromAPI(fileToRemove, isUpdate);

      // Remove from states
      removeFromStates(fileToRemove);

      // Update label
      updateLabel(fileList.length - 1);
    } catch (error) {
      console.error("Delete error:", error);
      setError("เกิดข้อผิดพลาดในการลบไฟล์");
    }
  };

  // Delete file from API
  const deleteFromAPI = async (fileToRemove, isUpdate) => {
    await Promise.all([
      portalService.DELETE_MEDIA_BY_PATH({ path: fileToRemove.path }),
      !isUpdate ? true : deleteApiUrl(id, { [paramDelete]: "" }),
    ]);
  };

  // Remove file from all states
  const removeFromStates = (fileToRemove) => {
    // Remove from file list
    setFileList((prev) => {
      const updatedList = prev.filter((f) => f.id !== fileToRemove.id);
      triggerCallbacks(updatedList);
      return updatedList;
    });

    // Clean up progress and status
    setUploadStatus((prev) => {
      const newStatus = { ...prev };
      delete newStatus[fileToRemove.id];
      return newStatus;
    });

    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[fileToRemove.id];
      return newProgress;
    });
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    accept: getAcceptedFileTypes(acceptType),
    disabled: disabled,
    multiple: maxFiles === 1 ? false : true,
    maxFiles: maxFiles,
    onError: (error) => {
      console.error("Dropzone error:", error);
      setError("เกิดข้อผิดพลาดในการเลือกไฟล์");
    },
  });

  // Sync with external value prop (only on initial load)
  useEffect(() => {
    if (value?.length > 0) {
      const validFiles = value.filter(
        (file) => file && !file.isDelete && (file.media_uri || file.name)
      );

      if (validFiles.length > 0) {
        // Only update if fileList is empty (initial load)
        setFileList([...validFiles]);
        updateLabel(validFiles.length);
      }
    }
    // Remove dependency on value to prevent clearing on every change
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render file item
  const renderFileItem = (file, i) => {
    if (file?.media_uri) {
      return (
        <div key={i} className="file_item">
          <div className="file_info">
            {/* ไฟล์ที่อัปแล้ว คลิกชื่อเพื่อเปิดดูในแท็บใหม่ */}
            <a
              className="file_name file_link"
              href={IMG_PATH + file.media_uri}
              target="_blank"
              rel="noopener noreferrer"
            >
              {file.media_uri.split("/")[file.media_uri.split("/").length - 1]}
            </a>
            <div className="file_size"></div>
          </div>
          {/* โหมดดูอย่างเดียว (disabled / isDelete=false) ห้ามมีปุ่มลบ
              เดิมโชว์เสมอ กดแล้วลบไฟล์จริงบน server ทั้งที่เป็นหน้าดูข้อมูล */}
          {isDelete && !disabled && (
            <div
              className="remove_file_btn"
              onClick={() => handleRemoveFile({ path: file.media_uri }, true)}
              title="ลบไฟล์"
            >
              <MdDelete fontSize={25} color={"red"} />
            </div>
          )}
        </div>
      );
    } else {
      const status = uploadStatus[file.id];
      const progress = uploadProgress[file.id] || 0;
      const showDeleteButton =
        isDelete &&
        (status === "success" ||
          status === "error" ||
          (!uploadApiUrl && !portalService?.POST_SURVEY_UPLOAD_MEDIA));
      return (
        <div key={file.id} className="file_item">
          <div className="file_info">
            <div className="file_name">
              {file.originalFileName || file.name}
            </div>
            <div className="file_size">
              {file.fileSize
                ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB`
                : file.size
                ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                : ""}
            </div>

            {/* Progress Bar */}
            {status === "uploading" && (
              <div className="file_progress_bottom">
                <div className="progress_bar_container">
                  <div
                    className="progress_bar"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="progress_info">
                  <span className="progress_text">
                    กำลังอัพโหลด... {progress}%
                  </span>
                </div>
              </div>
            )}

            {/* Status Messages */}
            {status === "success" && (
              <div className="file_status success">✅ อัพโหลดสำเร็จ</div>
            )}
            {status === "error" && (
              <div className="file_status error">❌ อัพโหลดล้มเหลว</div>
            )}
          </div>
          {showDeleteButton && (
            <div
              className="remove_file_btn"
              onClick={() => handleRemoveFile(file)}
              title="ลบไฟล์"
            >
              <MdDelete fontSize={25} color={"red"} />
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <UploadImageInputStyled
      className={theme_upload_image ? "theme_upload_image" : ""}
    >
      {label && <div className="label">{label}</div>}
      <div className="upload_container">
        <div
          {...getRootProps({
            className: `box_upload ${disabled ? "disabled" : ""} ${
              isDragActive ? "active" : ""
            }`,
          })}
        >
          <input {...getInputProps()} />
          <div className="box_path">
            <div className="path_name">
              {isDragActive ? "วางไฟล์ที่นี่" : labelShow}
            </div>
          </div>
          <div className="btn_upload">Browse</div>
        </div>
      </div>
      {scaleLabel && (
        <div className="input_upload_scale_label">{scaleLabel}</div>
      )}
      {fileList && fileList.length > 0 && (
        <div className="uploaded_files_list">
          <div className="list_header">ไฟล์ที่เลือก ({fileList.length})</div>
          {fileList.map(renderFileItem)}
        </div>
      )}
      {error && <div className="errors">{error}</div>}
      {errors && <div className="errors">{errors}</div>}
    </UploadImageInputStyled>
  );
};

UploadImageInput.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  onChangeCustom: PropTypes.func,
  acceptType: PropTypes.oneOf([
    "cover",
    "png",
    "jpg",
    "pdf",
    "csv",
    "json",
    "img-pdf",
    "video",
    "audio",
  ]),
  maxMBFileSizeWarning: PropTypes.number,
  maxFiles: PropTypes.number,
  fieldName: PropTypes.string,
  name: PropTypes.string,
  errors: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  scaleLabel: PropTypes.string,
  isDelete: PropTypes.bool,
  disabled: PropTypes.bool,
  uploadApiUrl: PropTypes.func,
  theme_upload_image: PropTypes.bool,
};

export default UploadImageInput;
